import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { legacyApi } from '../../services/legacyApi';
import { api } from '../../services/api';
import type { Account, APPaymentMode, ApOpenBill, AllocationLine, SupplierAgingSummaryRow } from '../../services/types';
import { toast } from 'sonner';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import { useSearchParams } from 'react-router-dom';

type PaymentMode = Extract<APPaymentMode, 'BILL_SETTLEMENT' | 'SUPPLIER_PREPAYMENT'>;

const MODE_LABELS: Record<PaymentMode, string> = {
    BILL_SETTLEMENT: 'Bill Settlement',
    SUPPLIER_PREPAYMENT: 'Supplier Prepayment',
};

export function APPaymentForm() {
    const [searchParams] = useSearchParams();
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<string>('');
    const [cashAccounts, setCashAccounts] = useState<Account[]>([]);
    const [selectedCashAccountId, setSelectedCashAccountId] = useState<string>('');
    const [mode, setMode] = useState<PaymentMode>('BILL_SETTLEMENT');
    const [openBills, setOpenBills] = useState<ApOpenBill[]>([]);
    const [allocationDraft, setAllocationDraft] = useState<Record<number, string>>({});
    const [amount, setAmount] = useState<number>(0);
    const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [reference, setReference] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [lastPost, setLastPost] = useState<{ payment_id?: number; journal_id?: number; mode?: string } | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [agingSummary, setAgingSummary] = useState<SupplierAgingSummaryRow[]>([]);
    const prefillSupplierId = searchParams.get('supplier_id');
    const prefillBillId = searchParams.get('bill_id');
    const prefillAmount = searchParams.get('amount');
    const prefillMode = searchParams.get('mode');

    useEffect(() => {
        legacyApi.suppliers.list().then(setSuppliers).catch(() => setSuppliers([]));
        api.financials
            .listAccounts()
            .then((rows) => {
                const candidates = rows.filter((account) => {
                    const label = `${account.code || ''} ${account.name || ''}`.toLowerCase();
                    return (
                        account.type === 'Asset'
                        && account.is_active !== false
                        && account.is_postable !== false
                        && (label.includes('cash') || label.includes('bank') || account.code === '1000' || account.parent_account_id === 1000)
                    );
                });
                setCashAccounts(candidates);
            })
            .catch(() => setCashAccounts([]));
    }, []);

    useEffect(() => {
        api.financials
            .listApAgingSummary()
            .then((payload) => setAgingSummary(payload.items || []))
            .catch(() => setAgingSummary([]));
    }, []);

    useEffect(() => {
        if (prefillMode === 'SUPPLIER_PREPAYMENT') {
            setMode('SUPPLIER_PREPAYMENT');
        } else if (prefillMode === 'BILL_SETTLEMENT') {
            setMode('BILL_SETTLEMENT');
        }
    }, [prefillMode]);

    useEffect(() => {
        if (!prefillSupplierId || selectedSupplier) return;
        setSelectedSupplier(prefillSupplierId);
    }, [prefillSupplierId, selectedSupplier]);

    useEffect(() => {
        if (!prefillAmount || amount > 0) return;
        const parsed = Number(prefillAmount);
        if (Number.isFinite(parsed) && parsed > 0) {
            setAmount(Number(parsed.toFixed(2)));
        }
    }, [prefillAmount, amount]);

    useEffect(() => {
        if (!selectedSupplier || mode !== 'BILL_SETTLEMENT') {
            setOpenBills([]);
            setAllocationDraft({});
            return;
        }
        api.financials.listApOpenBills(Number(selectedSupplier))
            .then(rows => {
                setOpenBills(rows);
                setAllocationDraft({});
            })
            .catch((error: any) => {
                console.error(error);
                toast.error(error?.message || 'Failed to load open supplier bills');
            });
    }, [selectedSupplier, mode]);

    useEffect(() => {
        if (!prefillBillId || openBills.length === 0) return;
        const targetBillId = Number(prefillBillId);
        if (!Number.isFinite(targetBillId)) return;
        const match = openBills.find(bill => bill.bill_id === targetBillId);
        if (!match) return;
        setAllocationDraft((prev) => {
            if (prev[targetBillId]) return prev;
            const apply = amount > 0 ? Math.min(amount, match.outstanding_amount) : match.outstanding_amount;
            return { ...prev, [targetBillId]: apply.toFixed(2) };
        });
    }, [prefillBillId, openBills, amount]);

    const allocationLines = useMemo<AllocationLine[]>(() => {
        return openBills
            .map(bill => {
                const value = parseFloat(allocationDraft[bill.bill_id] ?? '');
                if (!Number.isFinite(value) || value <= 0) return null;
                return { document_id: bill.bill_id, apply_amount: Number(value.toFixed(2)) };
            })
            .filter((line): line is AllocationLine => !!line);
    }, [allocationDraft, openBills]);

    const allocatedTotal = useMemo(() => {
        return Number(allocationLines.reduce((sum, line) => sum + line.apply_amount, 0).toFixed(2));
    }, [allocationLines]);

    const autoAllocate = () => {
        let remaining = Number(amount || 0);
        if (!Number.isFinite(remaining) || remaining <= 0) return;
        const next: Record<number, string> = {};
        openBills.forEach(bill => {
            if (remaining <= 0) return;
            const apply = Math.min(bill.outstanding_amount, remaining);
            if (apply > 0) {
                next[bill.bill_id] = apply.toFixed(2);
                remaining = Number((remaining - apply).toFixed(2));
            }
        });
        setAllocationDraft(next);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validation = validateRequiredFields([
            { key: 'selectedSupplier', label: 'Supplier', value: selectedSupplier, required: true },
            { key: 'selectedCashAccountId', label: 'Cash/Bank Account', value: selectedCashAccountId, required: true },
            { key: 'date', label: 'Date', value: date, required: true },
            { key: 'amount', label: 'Amount', value: amount > 0 ? amount : '', required: true },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;

        if (mode === 'BILL_SETTLEMENT') {
            if (allocationLines.length === 0) {
                nextErrors.allocations = 'At least one bill allocation is required.';
            } else if (Math.abs(allocatedTotal - Number(amount || 0)) > 0.01) {
                nextErrors.allocations = 'Allocation total must exactly match payment amount.';
            }
        }

        if (!validation.valid || Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField || 'allocations');
            return;
        }
        setFieldErrors({});

        setLoading(true);
        try {
            const result = await api.financials.postApPayment({
                supplier_id: parseInt(selectedSupplier, 10),
                mode,
                amount: Number(amount),
                date,
                reference,
                cash_account_id: parseInt(selectedCashAccountId, 10),
                allocations: mode === 'BILL_SETTLEMENT' ? allocationLines : [],
            });
            setLastPost({
                payment_id: Number(result?.payment_id || 0),
                journal_id: Number(result?.journal_id || 0),
                mode: String(result?.mode || mode),
            });
            toast.success('Payment posted successfully.');
            setAmount(0);
            setReference('');
            setAllocationDraft({});
            if (mode === 'BILL_SETTLEMENT' && selectedSupplier) {
                const rows = await api.financials.listApOpenBills(Number(selectedSupplier));
                setOpenBills(rows);
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to post payment');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto space-y-3">
            <FlowEntryBanner
                title="AP Payment Flow"
                pathLabel="Supplier Bill -> Payment Allocation"
                nextHint="Post supplier payments against specific bills for payable traceability."
                helpModule="finance"
            />
            <Card>
                <CardHeader>
                    <CardTitle>Supplier Payment</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-4 rounded-md border bg-muted/20 p-3">
                        <div className="mb-2 flex items-center justify-between text-xs">
                            <span className="font-medium">AP Aging Snapshot</span>
                            <span className="text-muted-foreground">{agingSummary.length} suppliers</span>
                        </div>
                        {agingSummary.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No open AP balances.</p>
                        ) : (
                            <div className="max-h-40 overflow-auto rounded border bg-background">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted/40">
                                        <tr>
                                            <th className="p-2 text-left">Supplier</th>
                                            <th className="p-2 text-right">Outstanding</th>
                                            <th className="p-2 text-right">Bucket</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {agingSummary.slice(0, 8).map((row) => (
                                            <tr key={row.supplier_id} className="border-t">
                                                <td className="p-2">{row.supplier_name}</td>
                                                <td className="p-2 text-right">{row.total_outstanding.toFixed(2)}</td>
                                                <td className="p-2 text-right">{row.age_bucket}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                        Finance impact: Credit Cash/Bank. Debit AP for bill settlements, or debit Supplier Advances for prepayments.
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="selectedSupplier">Supplier</RequiredLabel>
                                <select
                                    id="selectedSupplier"
                                    name="selectedSupplier"
                                    aria-invalid={!!fieldErrors.selectedSupplier}
                                    aria-required="true"
                                    className="w-full p-2 border rounded"
                                    value={selectedSupplier}
                                    onChange={(e) => setSelectedSupplier(e.target.value)}
                                    required
                                >
                                    <option value="">Select Supplier</option>
                                    {suppliers.map(s => (
                                        <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>
                                    ))}
                                </select>
                                <FieldErrorText message={fieldErrors.selectedSupplier} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="mode">Mode</RequiredLabel>
                                <select
                                    id="mode"
                                    name="mode"
                                    className="w-full p-2 border rounded"
                                    value={mode}
                                    onChange={(e) => setMode(e.target.value as PaymentMode)}
                                >
                                    <option value="BILL_SETTLEMENT">{MODE_LABELS.BILL_SETTLEMENT}</option>
                                    <option value="SUPPLIER_PREPAYMENT">{MODE_LABELS.SUPPLIER_PREPAYMENT}</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="date">Date</RequiredLabel>
                                <input
                                    id="date"
                                    name="date"
                                    aria-invalid={!!fieldErrors.date}
                                    aria-required="true"
                                    type="date"
                                    className="w-full p-2 border rounded"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    required
                                />
                                <FieldErrorText message={fieldErrors.date} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel required htmlFor="amount">Amount</RequiredLabel>
                                <input
                                    id="amount"
                                    name="amount"
                                    aria-invalid={!!fieldErrors.amount}
                                    aria-required="true"
                                    type="number"
                                    className="w-full p-2 border rounded"
                                    value={amount}
                                    onChange={(e) => setAmount(parseFloat(e.target.value || '0'))}
                                    required
                                    min="0.01"
                                    step="0.01"
                                />
                                <FieldErrorText message={fieldErrors.amount} />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Reference / Remarks</label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                placeholder="Cheque No, bank transfer ref, remarks"
                            />
                        </div>

                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="selectedCashAccountId">Cash/Bank Account</RequiredLabel>
                            <select
                                id="selectedCashAccountId"
                                name="selectedCashAccountId"
                                aria-invalid={!!fieldErrors.selectedCashAccountId}
                                aria-required="true"
                                className="w-full p-2 border rounded"
                                value={selectedCashAccountId}
                                onChange={(e) => setSelectedCashAccountId(e.target.value)}
                                required
                            >
                                <option value="">Select cash/bank account</option>
                                {cashAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>
                                        {account.code} - {account.name}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText message={fieldErrors.selectedCashAccountId} />
                        </div>

                        {mode === 'BILL_SETTLEMENT' && (
                            <div className="space-y-2 rounded-md border p-3">
                                <div className="flex items-center justify-between gap-3">
                                    <p className="text-sm font-medium">Bill Allocations</p>
                                    <Button type="button" variant="outline" size="sm" onClick={autoAllocate} disabled={openBills.length === 0}>
                                        Auto Allocate
                                    </Button>
                                </div>
                                {openBills.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">No open supplier bills found.</p>
                                ) : (
                                    <div className="max-h-56 overflow-auto rounded border">
                                        <table className="w-full text-sm">
                                            <thead className="bg-muted/40">
                                                <tr>
                                                    <th className="p-2 text-left font-medium">Bill</th>
                                                    <th className="p-2 text-right font-medium">Outstanding</th>
                                                    <th className="p-2 text-right font-medium">Apply</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {openBills.map(bill => (
                                                    <tr key={bill.bill_id} className="border-t">
                                                        <td className="p-2">{bill.bill_no}</td>
                                                        <td className="p-2 text-right">{bill.outstanding_amount.toFixed(2)}</td>
                                                        <td className="p-2">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max={bill.outstanding_amount}
                                                                className="w-full rounded border p-1 text-right"
                                                                value={allocationDraft[bill.bill_id] ?? ''}
                                                                onChange={(e) =>
                                                                    setAllocationDraft(prev => ({
                                                                        ...prev,
                                                                        [bill.bill_id]: e.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="flex items-center justify-between text-xs">
                                    <span>Allocated: {allocatedTotal.toFixed(2)}</span>
                                    <span>Difference: {(Number(amount || 0) - allocatedTotal).toFixed(2)}</span>
                                </div>
                                <FieldErrorText message={fieldErrors.allocations} />
                            </div>
                        )}

                        <Button type="submit" variant="destructive" disabled={loading} className="w-full">
                            {loading ? 'Posting...' : `Post ${MODE_LABELS[mode]}`}
                        </Button>
                    </form>
                    {lastPost && (
                        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                            Posted successfully. Payment #{lastPost.payment_id || '-'} | Journal #{lastPost.journal_id || '-'} | Mode {lastPost.mode || mode}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
